// =============================================================================
// app/src/lib/folha.ts — regras de cálculo da folha de pagamento.
//
// Vive fora do componente de propósito: regra de dinheiro precisa ser testável
// sem montar React. O que está aqui é a MESMA aritmética que o banco aplica na
// coluna gerada `payroll_entries.total` — se um dia divergirem, o banco vence,
// e o teste `total espelha a coluna gerada do banco` existe para pegar isso.
// =============================================================================

export interface ComposicaoFolha {
  salario_base: number;
  comissao: number;
  bonus: number;
  descontos: number;
}

/**
 * Total líquido do lançamento.
 * Espelha: total = salario_base + comissao + bonus - descontos (migração 008).
 */
export const totalFolha = (c: ComposicaoFolha): number =>
  c.salario_base + c.comissao + c.bonus - c.descontos;

/**
 * O dia em que ESTA pessoa recebe a folha desta competência (083).
 *
 * O mês curto ganha o mesmo tratamento da agenda e das contas fixas: quem
 * recebe no dia 31 recebe no último dia de fevereiro, em vez de a data
 * escorregar para março. `competencia` é o dia 1 do mês, como o banco exige.
 */
export function dataDoPagamento(competencia: string, dia: number): string {
  const [ano, mes] = competencia.split('-').map(Number) as [number, number];
  const ultimo = new Date(Date.UTC(ano, mes, 0)).getUTCDate();
  const escolhido = Math.min(Math.max(Math.trunc(dia) || 1, 1), ultimo);
  return `${competencia.slice(0, 7)}-${String(escolhido).padStart(2, '0')}`;
}

/**
 * O dia que o "Pagar" do Painel carimba no caixa (094): o vencimento, se ele
 * já passou — a folha costuma ser registrada depois de paga, e o dinheiro saiu
 * no dia da pessoa —, senão HOJE. Antes ia o vencimento cru, e quem pagava
 * adiantado lançava a saída num dia que ainda não chegou; o banco agora recusa
 * data futura. Sem vencimento (lançamento anterior à 083), hoje.
 */
export function diaDePagarAFolha(vencimento: string | null | undefined, hoje: string): string {
  return vencimento && vencimento < hoje ? vencimento : hoje;
}

/**
 * Confere o dia que o dono digitou no "Pago em" do Painel. diaDePagarAFolha é
 * só a sugestão: quem pagou num dia diferente do vencimento (feriado,
 * adiantamento) digita o dia real — pedido do dono em 19/10/2026. Futuro o
 * banco recusa (094); aqui a recusa chega antes do clique e com nome.
 */
export function validarDiaPago(dia: string, hoje: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dia)) return 'Informe o dia em que o pagamento foi feito.';
  if (dia > hoje) return 'O dia do pagamento não pode estar no futuro.';
  return null;
}

/**
 * Comissão sugerida sobre o que a pessoa vendeu no mês, arredondada ao centavo.
 * Sugestão apenas: o número final é decisão do dono e continua editável.
 */
export const comissaoSugerida = (vendidoNoMes: number, percent: number): number => {
  if (!Number.isFinite(vendidoNoMes) || !Number.isFinite(percent)) return 0;
  if (vendidoNoMes <= 0 || percent <= 0) return 0;
  return Math.round(vendidoNoMes * (percent / 100) * 100) / 100;
};

// --- A comissão e a linha prevista (094, revisão de 09/10/2026) ---------------
//
// A linha PREVISTA nasce do combinado com comissão ZERO: a geração automática
// (gerar_folha_prevista) não sabe quanto a pessoa vai vender no mês. Antes da
// 094 o mês começava SEM lançamento, e o campo de comissão do Painel abria na
// sugestão (vendido × %); o primeiro "Lançar na folha" já levava a comissão.
// Com a prevista existindo antes de o dono abrir o Painel, o `??` parava no
// zero da linha, a sugestão sumia e o "Pagar" — no Painel ou em Contas a
// pagar — saía só com o salário-base. As duas funções abaixo devolvem o que
// era antes, e o banco tem a mesma trava (pagar_folha recusa a prevista de
// quem tem comissão).

/** O mínimo de uma linha da folha que estas regras olham. */
interface LinhaDaComissao {
  prevista: boolean;
  pago_em: string | null;
  comissao: number;
}

/**
 * Com que valor o campo "Comissão" do Painel abre. Sem lançamento, a sugestão
 * (como sempre foi). Linha prevista ainda com comissão zero: a sugestão
 * também — o zero ali é "ninguém olhou", não decisão do dono. Linha que o
 * dono salvou (prevista = false): o que ele salvou, mesmo zero.
 */
export function comissaoDoCampo(
  lancamento: Pick<LinhaDaComissao, 'prevista' | 'comissao'> | null | undefined,
  sugerida: number,
): number {
  if (!lancamento) return sugerida;
  if (lancamento.prevista && Number(lancamento.comissao) === 0) return sugerida;
  return Number(lancamento.comissao);
}

/**
 * A linha ainda espera a comissão do mês? É a prevista, não paga, de quem tem
 * comissão no combinado. Enquanto for, ela NÃO se paga direto — nem no Painel
 * nem em Contas a pagar: o dono confere a comissão no Painel e salva (o que
 * grava prevista = false) e só então paga. Espelha a trava de pagar_folha.
 */
export function faltaAComissao(
  lancamento: Pick<LinhaDaComissao, 'prevista' | 'pago_em'> | null | undefined,
  comissaoPercent: number,
): boolean {
  return Boolean(lancamento && lancamento.prevista && !lancamento.pago_em)
    && Number(comissaoPercent) > 0;
}

/**
 * O lançamento pode ser gravado?
 * O banco tem CHECK de não-negativo em cada parcela e no total; validar antes
 * evita mandar para o servidor algo que voltaria como erro de constraint.
 */
export function validarFolha(c: ComposicaoFolha): string | null {
  const campos: Array<[keyof ComposicaoFolha, string]> = [
    ['salario_base', 'Salário'],
    ['comissao', 'Comissão'],
    ['bonus', 'Bônus'],
    ['descontos', 'Descontos'],
  ];

  for (const [campo, rotulo] of campos) {
    const v = c[campo];
    if (!Number.isFinite(v)) return `${rotulo}: valor inválido.`;
    if (v < 0) return `${rotulo} não pode ser negativo.`;
  }

  if (totalFolha(c) < 0) return 'Os descontos não podem passar do total a receber.';
  return null;
}

/** Um lançamento sem nada preenchido não deve virar linha no banco. */
export const folhaVazia = (c: ComposicaoFolha): boolean => totalFolha(c) === 0
  && c.salario_base === 0 && c.comissao === 0 && c.bonus === 0 && c.descontos === 0;
